import { iWhatsNewPost, iWhatsNewTag } from "../../apis/whatsNew.apis";

export type WhatsNewCategory = "new" | "improvements" | "fixes" | "beta";

export type PreparedPost = {
    post: iWhatsNewPost;
    tags: iWhatsNewTag[];
    image?: string;
    /** Short plain summary for the card body (first paragraph / clipped). */
    summary: string;
    html: string;
    category: WhatsNewCategory;
    categoryLabel: string;
    ctaLabel?: string;
    ctaUrl?: string;
};

export type MonthGroup = {
    key: string;
    label: string;
    entries: PreparedPost[];
};

const CAROUSEL_SIZE = 3;

const CATEGORY_LABELS: Record<WhatsNewCategory, string> = {
    new: "New Feature",
    improvements: "Improved",
    fixes: "Bug Fixes",
    beta: "Beta",
};

function pickImageField(post: iWhatsNewPost): string | undefined {
    return (
        post.image ||
        post.cover_image ||
        post.thumbnail ||
        post.image_url ||
        undefined
    )?.trim() || undefined;
}

function firstParagraphText(body: HTMLElement): string {
    const paragraph = body.querySelector("p");
    const text = (paragraph?.textContent || body.textContent || "").replace(/\s+/g, " ").trim();
    if (!text) return "";
    return text.length > 160 ? `${text.slice(0, 157).trimEnd()}…` : text;
}

function extractInlineImage(body: HTMLElement): string | undefined {
    const img = body.querySelector("img");
    if (img) {
        const src =
            img.getAttribute("src") ||
            img.getAttribute("data-src") ||
            img.getAttribute("data-original") ||
            undefined;
        img.remove();
        if (src?.trim()) return src.trim();
    }

    const source = body.querySelector("picture source[srcset], source[srcset]");
    if (source) {
        const candidate = (source.getAttribute("srcset") || "")
            .split(",")[0]
            ?.trim()
            .split(/\s+/)[0];
        source.closest("picture")?.remove();
        source.remove();
        if (candidate) return candidate;
    }

    const styled = body.querySelector<HTMLElement>("[style*='background-image']");
    if (styled) {
        const match = /url\((['"]?)(.*?)\1\)/i.exec(styled.getAttribute("style") || "");
        if (match?.[2]) {
            styled.removeAttribute("style");
            return match[2];
        }
    }

    return undefined;
}

const FEATURE_CTA_ROUTES: Array<{ match: RegExp; url: string }> = [
    { match: /\b(flow|automation|shipment|email)\b/i, url: "/email/automations" },
    { match: /\bcampaign\b/i, url: "/email/campaigns" },
    { match: /\bpopup\b/i, url: "/popups" },
    { match: /\bchat\b/i, url: "/chats" },
    { match: /\breview\b/i, url: "/reviews" },
    { match: /\bwishlist\b/i, url: "/wishlists" },
];

function resolveCta(post: iWhatsNewPost, body: HTMLElement): {
    label?: string;
    url?: string;
} {
    const label = (post.ctaLabel || post.cta_label || "").trim();
    const url = (post.ctaUrl || post.cta_url || "").trim();
    if (url) return { label: label || "Learn more", url };

    const inlineLink = body.querySelector<HTMLAnchorElement>("a[href]");
    const inlineUrl = inlineLink?.getAttribute("href")?.trim();
    if (inlineUrl) {
        return {
            label: label || inlineLink?.textContent?.replace(/\s+/g, " ").trim() || "Learn more",
            url: inlineUrl,
        };
    }

    const route = FEATURE_CTA_ROUTES.find(({ match }) => match.test(post.title));
    return route ? { label: label || "Explore feature", url: route.url } : {};
}

export function resolveCategory(tags: iWhatsNewTag[], title = ""): WhatsNewCategory {
    const haystack = `${tags.map((tag) => tag.name).join(" ")} ${title}`.toLowerCase();
    if (/\bbeta\b/.test(haystack)) return "beta";
    if (/\b(fix|fixes|fixed|bug|bugs)\b/.test(haystack)) return "fixes";
    if (/\b(improve|improved|improvement|improvements|enhance|enhanced|enhancement|enhancements)\b/.test(haystack)) {
        return "improvements";
    }
    return "new";
}

/**
 * Announcement bodies often embed media inline, so the first image is hoisted out to fill the
 * card's media slot and dropped from the copy to avoid showing it twice.
 */
export function prepare(post: iWhatsNewPost): PreparedPost {
    const tags = post.tags?.length ? post.tags : post.tag ? [post.tag] : [];
    const description = post.description || "";
    const body = new DOMParser().parseFromString(description, "text/html").body;
    const inlineImage = extractInlineImage(body);
    const cta = resolveCta(post, body);
    const summary = firstParagraphText(body);
    const html = body.innerHTML.trim() || description;
    const category = resolveCategory(tags, post.title);

    return {
        post,
        tags,
        image: pickImageField(post) || inlineImage,
        summary,
        html,
        category,
        categoryLabel: CATEGORY_LABELS[category],
        ctaLabel: cta.label,
        ctaUrl: cta.url,
    };
}

/**
 * Top carousel: featured posts first (backend), then fill from feed order up to 3.
 * Remaining posts render in the dated list below.
 */
export function splitCarousel(posts: iWhatsNewPost[]): {
    carousel: PreparedPost[];
    rest: PreparedPost[];
} {
    if (!posts.length) return { carousel: [], rest: [] };

    const isHighlighted = (post: iWhatsNewPost) => {
        const tags = post.tags?.length ? post.tags : post.tag ? [post.tag] : [];
        return Boolean(
            post.featured ||
            tags.some((tag) => tag.highlighted || tag.isHighlighted || tag.is_highlighted)
        );
    };
    const highlighted = posts.filter(isHighlighted);
    const others = posts.filter((post) => !isHighlighted(post));
    const ordered = highlighted.length ? [...highlighted, ...others] : posts;
    const carouselIds = new Set(ordered.slice(0, CAROUSEL_SIZE).map((post) => post.id));

    return {
        carousel: ordered.slice(0, CAROUSEL_SIZE).map(prepare),
        rest: posts.filter((post) => !carouselIds.has(post.id)).map(prepare),
    };
}

export function filterByCategory(
    entries: PreparedPost[],
    tab: "all" | WhatsNewCategory
): PreparedPost[] {
    if (tab === "all") return entries;
    return entries.filter((entry) => entry.category === tab);
}

export function groupByMonth(entries: PreparedPost[], now = new Date()): MonthGroup[] {
    const groups = new Map<string, MonthGroup>();

    for (const entry of entries) {
        const raw = entry.post.date ? new Date(entry.post.date) : now;
        const date = Number.isNaN(raw.getTime()) ? now : raw;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        const label = date.toLocaleString("en-US", { month: "long", year: "numeric" });
        const existing = groups.get(key);
        if (existing) {
            existing.entries.push(entry);
        } else {
            groups.set(key, { key, label, entries: [entry] });
        }
    }

    return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
}

/** Changelog-style dates: "Today", "2 days ago", or "June 28". */
export function formatChangelogDate(iso?: string | null, now = new Date()): string {
    if (!iso) return "";
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return "";

    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startThen = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const days = Math.round((startToday.getTime() - startThen.getTime()) / 86400000);

    if (days <= 0) return "Today";
    if (days === 1) return "1 day ago";
    if (days < 7) return `${days} days ago`;

    return then.toLocaleString("en-US", { month: "long", day: "numeric" });
}
